import config from "../../config";

// The API suffixes its auth cookie names with its own NODE_ENV:
//   `XSRF-TOKEN${suffix}`, suffix = NODE_ENV === "production" ? "" : `_${NODE_ENV}`.
// The web env (REACT_APP_ENV) uses different tokens (dev|qa|stage|production), so map
// web env -> API NODE_ENV before deriving the suffix.
//
// ⚠️ qa/stage/production are DEPLOY-UNVERIFIED: the mapping only holds if each deployed
// API process actually runs that NODE_ENV. Unit tests mock the cookie and CANNOT catch a
// name mismatch, so verify the real Set-Cookie / document.cookie name per env at runtime
// (see phase 3.4) — a wrong name silently 403s every mutating request.
const WEB_ENV_TO_API_NODE_ENV: Record<string, string> = {
  dev: "development",
  qa: "qa",
  stage: "stage",
  production: "production",
};

/**
 * Map a web env (`REACT_APP_ENV`) to the suffix the API appends to its auth cookie names.
 *
 * The API derives the suffix from its own `NODE_ENV` (`""` for production, otherwise
 * `_${NODE_ENV}`), so the web env is translated to the matching API `NODE_ENV` first.
 * Unknown envs pass through unchanged and become their own suffix.
 *
 * @param webEnv - The web environment (e.g. `"dev"`, `"qa"`, `"stage"`, `"production"`).
 * @returns The cookie-name suffix (e.g. `"_development"`, `"_qa"`, or `""` for production).
 */
export const apiEnvSuffix = (webEnv: string): string => {
  const nodeEnv = WEB_ENV_TO_API_NODE_ENV[webEnv] ?? webEnv;
  return nodeEnv === "production" ? "" : `_${nodeEnv}`;
};

const suffix = apiEnvSuffix(config.env);

/** Name of the http-only refresh-token cookie the API sets. */
export const AUTH_COOKIE_NAME = `refreshToken${suffix}`;

/** Name of the JS-readable CSRF cookie the API sets (double-submit). */
export const CSRF_COOKIE_NAME = `XSRF-TOKEN${suffix}`;

/** Header the API expects the CSRF token echoed back in. */
export const CSRF_HEADER_NAME = "X-CSRF-Token";
